import type { BrandLockRule, LockType, ProductBrand, ProductCategory } from "@/types";
import { formatDate } from "@/lib/format";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

type ColDef = {
  header: string;
  width: number;
  align: "left" | "right";
};

const COLS: ColDef[] = [
  { header: "End Client",     width: 140, align: "left" },
  { header: "Category",       width: 110, align: "left" },
  { header: "Lock Type",      width: 110, align: "left" },
  { header: "Allowed Brands", width: 340, align: "left" },
  { header: "Valid From",     width: 130, align: "left" },
  { header: "Valid To",       width: 130, align: "left" },
  { header: "Status",         width:  90, align: "left" },
];

export function isRuleExpired(rule: BrandLockRule, today = new Date()): boolean {
  if (!rule.active) return true;
  if (!rule.validTo) return false;
  return new Date(rule.validTo + "T00:00:00") < today;
}

export function LockTypePill({ type, muted }: { type: BrandLockRule["lockType"]; muted: boolean }) {
  const strict = type === "Strict";
  const fg = muted ? semanticColors.textPlaceholder : (strict ? colors.red600 : colors.amber600);
  const bg = muted ? semanticColors.hoverBg : (strict ? colors.red50 : colors.amber50);
  const bd = muted ? semanticColors.border : (strict ? colors.red100 : colors.amber200);
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center",
        height: heights.pill, padding: `0 ${spacing[8]}px`, borderRadius: radius.base,
        fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
        color: fg, backgroundColor: bg, border: `1px solid ${bd}`,
      }}
    >
      {type}
    </span>
  );
}

// Real per-client brand-lock evaluation, used by the New Quote brand preview
// and the Add Materials picker. Distinct from — and not used by —
// MaterialTable's own isBrandViolation in services/pricing.ts, which is a
// simplified placeholder (category hardcoded to a single required OEM,
// ignores endClient entirely). Callers pass already client-filtered,
// non-expired rules (see isRuleExpired above).
export interface BrandLockEvaluation {
  lockType: LockType | null; // null = no applicable rule for this category
  allowedBrands: ProductBrand[];
  rule: BrandLockRule | null;
}

export function evaluateBrandLock(
  rules: BrandLockRule[],
  category: ProductCategory,
): BrandLockEvaluation {
  const applicable = rules.filter((r) => r.category === category || r.category === "All");
  if (applicable.length === 0) return { lockType: null, allowedBrands: [], rule: null };
  // A rule scoped to this exact category takes precedence over a client's
  // catch-all "All" rule (e.g. Brightwater: Copper Strict beats the general
  // Preferred "All" rule for a Copper item).
  const exact = applicable.find((r) => r.category === category);
  const chosen = exact ?? applicable[0];
  return { lockType: chosen.lockType, allowedBrands: chosen.allowedBrands, rule: chosen };
}

export type BrandLockViolationLevel = "strict" | "preferred" | null;

export function brandLockViolationLevel(
  evaluation: BrandLockEvaluation,
  brand: ProductBrand,
): BrandLockViolationLevel {
  if (evaluation.lockType === null) return null;
  if (evaluation.allowedBrands.includes(brand)) return null;
  return evaluation.lockType === "Strict" ? "strict" : "preferred";
}

// The rule-list display shared by the New Quote modal's brand preview and
// the Add Materials picker's brand banner — same rows, same "no locks"
// message, different outer framing per caller. testIdPrefix follows the
// DetailPanel.tsx precedent: each caller keeps its own existing testid
// convention (New Quote modal's `new-quote-brand-*` predates this
// extraction and must not change) instead of every caller sharing one name.
export function BrandRuleList({ rules, testIdPrefix }: { rules: BrandLockRule[]; testIdPrefix: string }) {
  if (rules.length === 0) {
    return (
      <div data-testid={`${testIdPrefix}-preview-empty`} style={{ fontSize: typography.fontSize.sm, color: semanticColors.textPlaceholder }}>
        Brand locks are not configured — no authoritative rules are available.
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: spacing[6] }}>
      {rules.map((r) => (
        <div key={r.id} data-testid={`${testIdPrefix}-rule-${r.id}`} className="flex items-center" style={{ gap: spacing[8] }}>
          <span style={{ fontSize: typography.fontSize.sm, color: semanticColors.textPrimary, minWidth: 70 }}>{r.category}</span>
          <LockTypePill type={r.lockType} muted={false} />
          <div className="flex items-center" style={{ gap: spacing[4], flexWrap: "wrap" }}>
            {r.allowedBrands.map((b) => <BrandPill key={b} brand={b} muted={false} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

export function BrandPill({ brand, muted }: { brand: string; muted: boolean }) {
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center",
        height: heights.pill, padding: `0 ${spacing[8]}px`, borderRadius: radius.base,
        fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
        color: muted ? semanticColors.textPlaceholder : semanticColors.textSecondary,
        backgroundColor: semanticColors.hoverBg,
        border: `1px solid ${semanticColors.border}`,
      }}
    >
      {brand}
    </span>
  );
}

function StatusPill({ expired }: { expired: boolean }) {
  const fg = expired ? semanticColors.textMuted : colors.green600;
  const bg = expired ? semanticColors.hoverBg : colors.green50;
  const bd = expired ? semanticColors.border : colors.green100;
  return (
    <span
      data-testid={`rule-status-${expired ? "expired" : "active"}`}
      style={{
        display: "inline-flex", alignItems: "center",
        height: heights.pill, padding: `0 ${spacing[8]}px`, borderRadius: radius.base,
        fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
        color: fg, backgroundColor: bg, border: `1px solid ${bd}`,
      }}
    >
      {expired ? "Expired" : "Active"}
    </span>
  );
}

export function BrandLockTable({
  rows,
  selectedId,
  onRowClick,
}: {
  rows: BrandLockRule[];
  selectedId?: string;
  onRowClick: (r: BrandLockRule) => void;
}) {
  return (
    <div
      data-testid="brand-lock-table"
      style={{
        border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
        backgroundColor: semanticColors.surface, overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex", height: heights.control,
          backgroundColor: semanticColors.panelBg, borderBottom: `1px solid ${semanticColors.border}`,
        }}
      >
        {COLS.map((c) => (
          <div
            key={c.header}
            style={{
              width: c.width, minWidth: c.width, padding: `0 ${spacing[12]}px`,
              display: "flex", alignItems: "center",
              justifyContent: c.align === "right" ? "flex-end" : "flex-start",
              fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: semanticColors.textSecondary,
              textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
              whiteSpace: "nowrap",
            }}
          >
            {c.header}
          </div>
        ))}
      </div>

      {/* Body */}
      <div>
        {rows.map((r) => {
          const expired = isRuleExpired(r);
          const textColor = expired ? semanticColors.textPlaceholder : semanticColors.textPrimary;
          const isSelected = r.id === selectedId;
          return (
            <div
              key={r.id}
              data-testid={`rule-row-${r.id}`}
              data-expired={expired ? "true" : "false"}
              className="material-row"
              onClick={() => onRowClick(r)}
              style={{
                display: "flex", height: heights.row,
                borderBottom: `1px solid ${semanticColors.border}`,
                backgroundColor: isSelected ? semanticColors.hoverBg : semanticColors.surface,
                cursor: "pointer",
              }}
            >
              <div style={{ width: COLS[0].width, minWidth: COLS[0].width, padding: `0 ${spacing[12]}px`,
                            display: "flex", alignItems: "center", fontSize: typography.fontSize.sm,
                            color: textColor, fontWeight: typography.fontWeight.medium }}>
                {r.endClient}
              </div>
              <div style={{ width: COLS[1].width, minWidth: COLS[1].width, padding: `0 ${spacing[12]}px`,
                            display: "flex", alignItems: "center", fontSize: typography.fontSize.sm, color: textColor }}>
                {r.category}
              </div>
              <div style={{ width: COLS[2].width, minWidth: COLS[2].width, padding: `0 ${spacing[12]}px`,
                            display: "flex", alignItems: "center" }}>
                <LockTypePill type={r.lockType} muted={expired} />
              </div>
              <div style={{ width: COLS[3].width, minWidth: COLS[3].width, padding: `0 ${spacing[12]}px`,
                            display: "flex", alignItems: "center", gap: spacing[4],
                            overflow: "hidden", whiteSpace: "nowrap" }}>
                {r.allowedBrands.map((b) => (
                  <BrandPill key={b} brand={b} muted={expired} />
                ))}
              </div>
              <div style={{ width: COLS[4].width, minWidth: COLS[4].width, padding: `0 ${spacing[12]}px`,
                            display: "flex", alignItems: "center", fontSize: typography.fontSize.sm,
                            color: textColor, fontVariantNumeric: "tabular-nums" }}>
                {formatDate(r.validFrom)}
              </div>
              <div style={{ width: COLS[5].width, minWidth: COLS[5].width, padding: `0 ${spacing[12]}px`,
                            display: "flex", alignItems: "center", fontSize: typography.fontSize.sm,
                            color: r.validTo ? textColor : semanticColors.textPlaceholder,
                            fontVariantNumeric: "tabular-nums" }}>
                {r.validTo ? formatDate(r.validTo) : "—"}
              </div>
              <div style={{ width: COLS[6].width, minWidth: COLS[6].width, padding: `0 ${spacing[12]}px`,
                            display: "flex", alignItems: "center" }}>
                <StatusPill expired={expired} />
              </div>
            </div>
          );
        })}
        {rows.length === 0 && (
          <div
            data-testid="rules-empty"
            style={{
              padding: spacing[40], textAlign: "center",
              fontSize: typography.fontSize.base, color: semanticColors.textPlaceholder,
            }}
          >
            No rules match the current filters.
          </div>
        )}
      </div>
    </div>
  );
}
